import React, { useEffect } from "react";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";

import ContentsDispatcher from "./ContentsDispatcher";
import { useAlert } from "../../../hooks/useAlert";
import { doPlanDataUpdateState, planDataRevisionState, useFlightPlan } from "../../../store/FlightPlanState";
import { highlightPointsState } from "../../../store/HighlightPointsState";
import { routePointEditingState } from "../../../store/RoutePointEditingState";
import { mapContentsVisibleWithID } from "../../../store/MapContentsState";
import { flightPlanContentsVisibleState } from "../../../store/FlightPlanListViewState";

const FlightPlanListDispatcher = (props) => {
  const {showError} = useAlert();
  const {
    flightPlanList, detail, editState, setFlightPlanList, setDetail, editFlightPlan, 
    highlightPlan, setHighlightPlan, clearHighlightPlan, planUpdated, needsRefreshPlan,
    visibilityChangeFlightPlan, draggingPoint, clearDragging, setFlightPlanLoadError
  } = useFlightPlan();
  const [highlightPoints, setHighlightPoints] = useRecoilState(highlightPointsState);
  const setRoutePointEditing = useSetRecoilState(routePointEditingState);

  const flightPlanListVisible = useRecoilValue(mapContentsVisibleWithID(props.contentsID));
  const setContentsVisible = useSetRecoilState(flightPlanContentsVisibleState);

  //パネル側でmapContentsVisibleWithIDを直接参照するとマップ中心座標が狂う問題があるため、DispatcherでON/OFF状態をこのstateに移し替える
  useEffect(() => {
    setContentsVisible(flightPlanListVisible);
  }, [flightPlanListVisible, setContentsVisible]);

  useEffect(() => {
    props.contents.onError((msg, err, param) => {
      showError(msg, {timeout: 5000});
      const e = err ? err.message : '';
      const p = param ? JSON.stringify(param) : '';
      console.log(`${msg} : ${e} \n parameter : ${p}`);
    });
  },[props.contents, showError]);

  useEffect(() => {
    const dispatcher = (action) => {
      if (action.type === 'SET_FLIGHT_PLAN_LIST') {
        setFlightPlanList(action.payload);
        setFlightPlanLoadError(false);
        planUpdated();
      } else if (action.type === 'START_EDIT') {
        setDetail(action.payload, true);
      } else if (action.type === 'EDIT') {
        editFlightPlan(action.payload);
        if (draggingPoint) {
          clearDragging();
        }
      } else if (action.type === 'HIDE_PLAN') {
        visibilityChangeFlightPlan(action.payload, false);
      } else if (action.type === 'END_DRAG') {
        clearDragging();
      } else if (action.type === 'DATA_LOAD_ERROR') {
        setFlightPlanLoadError(true);
      }
    }
    props.contents.setDispatcher(dispatcher);
  },[props.contents, setFlightPlanList, setDetail, editFlightPlan, planUpdated, visibilityChangeFlightPlan, draggingPoint, clearDragging, setFlightPlanLoadError]);

  //FlightPlanListのediting変数の変更を検知してルート編集中ステートを更新
  useEffect(() => {
    let editing = props.contents.editing;
    Object.defineProperty(props.contents, 'editing', {
      get: () => {
        return editing;
      },
      set: newValue => {
        editing = newValue;
        setRoutePointEditing(editing);
      }
    });
  }, [props.contents, setRoutePointEditing]);

  useEffect(() => {
    props.contents.onHighlightPoints((points) => {
      setHighlightPoints(points);
    });
  }, [props.contents, setHighlightPoints]);

  useEffect(() => {
    props.contents.onHighlightPlan((data) => {
      if (data.plan) {
        setHighlightPlan(data);
      } else {
        clearHighlightPlan();
      }
    });
  }, [props.contents, setHighlightPlan, clearHighlightPlan]);

  useEffect(() => {
    props.contents.highlightPoints(highlightPoints);
  }, [props.contents, highlightPoints]);

  useEffect(() => {
    props.contents.highlightPlan(highlightPlan);
  }, [props.contents, highlightPlan]);

  useEffect(() => {
    props.contents.setDraggingPoint(draggingPoint);
  }, [props.contents, draggingPoint]);

  useEffect(() => {
    props.contents.setFlightPlanList(flightPlanList);
  }, [props.contents, flightPlanList]);

  useEffect(() => {
    props.contents.setTarget(detail, editState.editing);
  }, [props.contents, detail, editState.editing]);

  useEffect(() => {
    if (needsRefreshPlan) {
      props.contents.reloadPlan();
    }
  }, [props.contents, needsRefreshPlan]);

  const [doPlanDataUpdate, setDoPlanDataUpdate] = useRecoilState(doPlanDataUpdateState);
  useEffect(() => {
    props.contents.dataUpdate(doPlanDataUpdate);
    setDoPlanDataUpdate(false);
  }, [props.contents, doPlanDataUpdate, setDoPlanDataUpdate]);

  const setPlanDataRevision = useSetRecoilState(planDataRevisionState);
  props.contents.setPlanDataRevisionDispatcher(() => {
    setPlanDataRevision(prev => prev + 1);
  });

  return <ContentsDispatcher {...props}/>
};

export default FlightPlanListDispatcher;